import React, { useEffect, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import Navigate from "../../components/Navigate";
import ResponsiveActionBar from "../../components/ResponsiveActionBar";
import ResponsiveTableWrapper from "../../components/ResponsiveTableWrapper";
import { exportToCSV, exportToPDF, notifyUser } from "../../util/util";
import { useAllMembers } from "../../hooks/api";

// Add this CSS to force text wrapping
const tableStyles = `
  .ant-table td, .ant-table th {
    white-space: normal !important;
    word-wrap: break-word !important;
  }
  .ant-table-cell {
    white-space: normal !important;
    word-break: break-word !important;
  }
`;

const MembersTable = () => {
    const [exportLoading, setExportLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    
    // Use React Query hook for members data
    const {
        data: members,
        isLoading,
        error,
        refetch
    } = useAllMembers();
    
    // Process and filter data for table display
    const filteredData = useMemo(() => {
        const tableData = members?.map(member => member.originalData) || [];
        
        if (!searchText) return tableData;
        
        return tableData.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(searchText.toLowerCase())
            )
        );
    }, [members, searchText]);
    
    // Update pagination total when filtered data changes
    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            total: filteredData.length,
        }));
    }, [filteredData]);

    // Inject the styles when component mounts
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.innerHTML = tableStyles;
        document.head.appendChild(styleElement);
        
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    const handleSearch = (e) => {
        setSearchText(e.target.value);
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const columns = [
        { 
            title: 'Member Number', 
            dataIndex: 'member_no', 
            key: 'member_no', 
            width: 180,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Full Name', 
            dataIndex: 'FullName', 
            key: 'FullName', 
            width: 150,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Date of Birth', 
            dataIndex: 'dob', 
            key: 'dob', 
            width: 120,
            render: text => {
                let formatted = '';
                if (text) {
                    const d = dayjs(text);
                    formatted = d.isValid() ? d.format('MMM D, YYYY') : text;
                }
                return <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatted}</div>;
            }
        },
        { 
            title: 'SSNIT Number', 
            dataIndex: 'ssnit_number', 
            key: 'ssnit_number', 
            width: 120,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Ghana Card Number', 
            dataIndex: 'GIN', 
            key: 'GIN', 
            width: 150,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Mobile Number', 
            dataIndex: 'mobile_no', 
            key: 'mobile_no', 
            width: 120, 
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>
        },
        { 
            title: 'Email', 
            dataIndex: 'email', 
            key: 'email', 
            width: 200,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || 'N/A'}</div>
        },
        { 
            title: 'Monthly Salary', 
            dataIndex: 'monthly_salary', 
            key: 'monthly_salary', 
            width: 130,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Monthly Contribution', 
            dataIndex: 'MonthlyContribution', 
            key: 'MonthlyContribution', 
            width: 150,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        }
    ];

    const exportFunction = async (type) => {
        setExportLoading(true);
        try {
            const exportData = filteredData || [];
            if (type === 'pdf') {
                exportToPDF(columns, exportData, 'Members', true);
            } else {
                exportToCSV(exportData, 'Members');
            }
        } catch (error) {
            notifyUser('error', 'Export failed', 'Error');
        } finally {
            setExportLoading(false);
        }
    };

    if (error) {
        return (
            <div className="p-4">
                <div className="alert alert-danger">
                    Error loading members: {error.message}
                    <button className="btn btn-sm btn-primary ms-2" onClick={refetch}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <ResponsiveActionBar
                title="Members List"
                searchValue={searchText}
                onSearchChange={handleSearch}
                onExportCSV={() => exportFunction('csv')}
                onExportPDF={() => exportFunction('pdf')}
                exportLoading={exportLoading}
                extraActions={
                    <Navigate path='/dashboard/members/new'>
                        <button className="btn btn-primary">
                            CREATE MEMBER <i className="bi bi-plus-circle" />
                        </button>
                    </Navigate>
                }
            />
            
            <ResponsiveTableWrapper
                columns={columns}
                dataSource={filteredData}
                loading={isLoading}
                rowKey="ssnit_number"
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} members`,
                }}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default MembersTable;
